import type { IsoProjection } from '../core/iso';
import type { WorldPoint } from '../core/types';

/**
 * Pure camera maths, matching Phaser 3's camera model (zoom about the
 * viewport centre, `scroll` = world coordinate shown at the viewport's
 * top-left at zoom 1). Kept engine-free so it is unit tested.
 */
export interface ViewState {
  scrollX: number;
  scrollY: number;
  zoom: number;
  /** Viewport size in screen pixels. */
  viewWidth: number;
  viewHeight: number;
}

export function clampZoom(zoom: number, min: number, max: number): number {
  if (!Number.isFinite(zoom)) return min;
  return Math.min(max, Math.max(min, zoom));
}

/**
 * Screen-space pivot the renderer zooms about. Phaser 3.90 (Camera.preRender)
 * rounds the pivot to a whole pixel — floor(w/2 + 0.5) — but translates back
 * by the UNrounded w/2. For odd viewport sizes that shifts everything drawn by
 * 0.5 px, so picking must use the same pivot to match what is on screen.
 * (Found in master Phase 1 validation; see DECISIONS D28.)
 */
export function screenPivot(v: Pick<ViewState, 'viewWidth' | 'viewHeight'>): { x: number; y: number } {
  return { x: Math.floor(v.viewWidth / 2 + 0.5), y: Math.floor(v.viewHeight / 2 + 0.5) };
}

/** World point under a screen point (screen relative to the canvas). */
export function screenToWorld(v: ViewState, sx: number, sy: number): WorldPoint {
  const p = screenPivot(v);
  return {
    x: (sx - p.x) / v.zoom + v.scrollX + v.viewWidth / 2,
    y: (sy - p.y) / v.zoom + v.scrollY + v.viewHeight / 2,
  };
}

/** Screen point (canvas CSS px) of a world point; exact inverse of screenToWorld. */
export function worldToScreen(v: ViewState, wx: number, wy: number): { x: number; y: number } {
  const p = screenPivot(v);
  return {
    x: (wx - v.scrollX - v.viewWidth / 2) * v.zoom + p.x,
    y: (wy - v.scrollY - v.viewHeight / 2) * v.zoom + p.y,
  };
}

/** Scroll that puts `world` under screen point (sx, sy) at the given zoom. */
export function scrollForScreenAnchor(v: Pick<ViewState, 'viewWidth' | 'viewHeight'>, zoom: number, world: WorldPoint, sx: number, sy: number): { scrollX: number; scrollY: number } {
  const p = screenPivot(v);
  return {
    scrollX: world.x - (sx - p.x) / zoom - v.viewWidth / 2,
    scrollY: world.y - (sy - p.y) / zoom - v.viewHeight / 2,
  };
}

/** World point at the centre of the viewport. */
export function viewCenter(v: ViewState): WorldPoint {
  return { x: v.scrollX + v.viewWidth / 2, y: v.scrollY + v.viewHeight / 2 };
}

/** Scroll values that place a world point at the viewport centre. */
export function scrollForCenter(v: Pick<ViewState, 'viewWidth' | 'viewHeight'>, wx: number, wy: number): { scrollX: number; scrollY: number } {
  return { scrollX: wx - v.viewWidth / 2, scrollY: wy - v.viewHeight / 2 };
}

/**
 * New view after zooming so the world point under (sx, sy) stays under the
 * cursor — the standard "zoom towards pointer" behaviour.
 */
export function zoomAtScreenPoint(v: ViewState, newZoom: number, sx: number, sy: number): ViewState {
  const anchor = screenToWorld(v, sx, sy);
  return { ...v, zoom: newZoom, ...scrollForScreenAnchor(v, newZoom, anchor, sx, sy) };
}

/** Pan by a screen-space delta (drag): world moves with the pointer. */
export function panByScreenDelta(v: ViewState, dx: number, dy: number): ViewState {
  return { ...v, scrollX: v.scrollX - dx / v.zoom, scrollY: v.scrollY - dy / v.zoom };
}

/**
 * Keep the viewport centre over the map diamond (plus a padding in tiles),
 * so the player can never lose the map off-screen at any zoom level.
 * Clamping happens in grid space, which follows the diamond exactly.
 */
export function clampToMap(v: ViewState, iso: IsoProjection, mapW: number, mapH: number, paddingTiles: number): ViewState {
  const c = viewCenter(v);
  const g = iso.worldToGridFloat(c.x, c.y);
  const gx = Math.min(mapW - 1 + paddingTiles, Math.max(-paddingTiles, g.x));
  const gy = Math.min(mapH - 1 + paddingTiles, Math.max(-paddingTiles, g.y));
  if (gx === g.x && gy === g.y) return v;
  const w = iso.gridToWorld(gx, gy);
  return { ...v, ...scrollForCenter(v, w.x, w.y) };
}
